import { type ChannelAlias, fromBackendChannel, toBackendChannel } from "../channel-map";
import { type ApiFetchContext, apiFetch } from "../client";
import type { BackendLang } from "../headers";
import {
  buildProductDetailUrl,
  buildProductHotSaleUrl,
  buildProductImageSearchUrl,
  buildProductPdpRecommendUrl,
  buildProductQcPhotosUrl,
  buildProductRecommendUrl,
  buildProductSearchPagedUrl,
  type ProductDetailInput,
  type ProductHotSaleInput,
  type ProductImageSearchInput,
  type ProductPdpRecommendInput,
  type ProductQcPhotosInput,
  type ProductRecommendInput,
  type ProductSearchPagedInput,
} from "../path-adapter";
import type {
  DetailAttribute,
  DetailAttributeVoRaw,
  PagedVo,
  ProductPdpRecommendVO,
  ProductPdpRecommendVORaw,
  ProductQcPhotos,
  ProductQcPhotosRaw,
  ProductRecommendVO,
  ProductRecommendVORaw,
  SpuDetail,
  SpuDetailSku,
  SpuDetailSkuVoRaw,
  SpuDetailVoRaw,
  SpuListVo,
  SpuListVoRaw,
} from "../types/product";

function normalizeSpu(raw: SpuListVoRaw): SpuListVo {
  return {
    spuNo: raw.spuNo,
    channel: fromBackendChannel(raw.channel),
    shopId: raw.shopId,
    shopName: raw.shopName,
    title: raw.subject,
    shortTitle: raw.productName,
    imageUrl: raw.mainImg,
    monthSold: raw.monthSold,
    priceRubKopecks: Math.round(raw.price * 100),
    cnyFen: Math.round(raw.cnyprice * 100),
    tagList: raw.tagList,
    spuLock: raw.spuLock,
    // #294: per-user favorite flag; absent on older/unauthed responses → false.
    isFavorite: raw.isFavorite ?? false,
  };
}

function normalizeRecommend(raw: ProductRecommendVORaw): ProductRecommendVO {
  return {
    spuNo: raw.spuNo,
    channel: fromBackendChannel(raw.channel),
    title: raw.subject,
    shortTitle: raw.productName,
    imageUrl: raw.mainImg,
    priceRubKopecks: Math.round(raw.price * 100),
    cnyFen: Math.round(raw.cnyprice * 100),
  };
}

function normalizePdpRecommend(raw: ProductPdpRecommendVORaw): ProductPdpRecommendVO {
  return {
    spuNo: raw.spuNo,
    channel: fromBackendChannel(raw.channel),
    imageUrl: raw.mainImg,
    title: raw.title,
    priceRubKopecks: Math.round(raw.price * 100),
    shopId: raw.shopId,
    shopName: raw.shopName,
  };
}

function normalizeQcPhotos(raw: ProductQcPhotosRaw): ProductQcPhotos {
  return {
    skuNo: raw.skuNo,
    channel: fromBackendChannel(raw.channel),
    spuNo: raw.spuNo,
    photos: (raw.photos ?? []).filter(
      (key): key is string => typeof key === "string" && key !== "",
    ),
    source: raw.source ?? null,
  };
}

function normalizeAttr(raw: DetailAttributeVoRaw): DetailAttribute {
  return {
    code: raw.code ?? "",
    name: raw.name ?? "",
    enumValue: raw.enumValue ?? "",
    enumImageUrl: raw.enumImageUrl,
  };
}

function normalizeSku(raw: SpuDetailSkuVoRaw): SpuDetailSku {
  const hasOriginal = raw.originalPrice != null && raw.originalPrice > 0;
  return {
    skuNo: raw.skuNo,
    spuNo: raw.spuNo,
    channel: fromBackendChannel(raw.channel),
    attrs: (raw.skuAttr ?? []).map(normalizeAttr),
    // 1688 batch-quote products price only at SPU level — per-SKU price/priceCNY
    // arrive null. Keep null (NOT 0) so consumers fall back to detail.price via
    // `activeSku?.priceRubKopecks ?? detail.priceRubKopecks` instead of showing
    // "0 ₽ / ¥0" (issue #424). Math.round(null * 100) would silently yield 0.
    priceRubKopecks: raw.price != null ? Math.round(raw.price * 100) : null,
    cnyFen: raw.priceCNY != null ? Math.round(raw.priceCNY * 100) : null,
    stock: raw.stock,
    priceMode: raw.priceMode,
    discount: raw.discount,
    originalPriceRubKopecks: hasOriginal ? Math.round((raw.originalPrice as number) * 100) : null,
    originalCnyFen:
      raw.originalPriceCNY != null && raw.originalPriceCNY > 0
        ? Math.round(raw.originalPriceCNY * 100)
        : null,
  };
}

function normalizeDetail(raw: SpuDetailVoRaw): SpuDetail {
  const hasOriginal = raw.originalPrice != null && raw.originalPrice > 0;
  return {
    spuNo: raw.spuNo,
    channel: fromBackendChannel(raw.channel),
    title: raw.subject,
    description: raw.description,
    imageUrl: raw.mainImg,
    imageList: raw.imageList ?? [],
    detailUrl: raw.detailUrl,
    attributes: (raw.baseAttrList ?? []).map(normalizeAttr),
    monthSold: raw.monthSold,
    startQuantity: raw.startQuantity ?? 1,
    priceRubKopecks: Math.round(raw.price * 100),
    cnyFen: Math.round(raw.priceCNY * 100),
    domesticExpressFeeKopecks: raw.expressFee != null ? Math.round(raw.expressFee * 100) : null,
    skuList: (raw.skuVoList ?? []).map(normalizeSku),
    tagList: raw.tagList ?? [],
    favorite: raw.favorite ?? false,
    sevenDaysRefunds: raw.sevenDaysRefunds ?? false,
    warehouseArrivalTime: raw.warehouseArrivalTime,
    priceMode: raw.priceMode,
    discount: raw.discount,
    originalPriceRubKopecks: hasOriginal ? Math.round((raw.originalPrice as number) * 100) : null,
    originalCnyFen:
      raw.originalPriceCNY != null && raw.originalPriceCNY > 0
        ? Math.round(raw.originalPriceCNY * 100)
        : null,
    showQc: (raw.showQc ?? 0) > 0,
    shopId: raw.shopId,
    notSupportQcVideos: raw.notSupportQcVideos ?? false,
    productGrayscale: raw.productGrayscale ?? false,
    productGrayscaleName: raw.productGrayscaleName,
    addCartFlag: raw.addCartFlag ?? true,
    editPriceFlag: raw.editPriceFlag ?? true,
    useCouponFlag: raw.useCouponFlag ?? true,
  };
}

function normalizePaged<TRaw, T>(paged: PagedVo<TRaw>, fn: (raw: TRaw) => T): PagedVo<T> {
  return {
    records: paged.records.map(fn),
    total: paged.total,
    size: paged.size,
    current: paged.current,
    pages: paged.pages,
  };
}

/** Subset of {@link ProductReqOpts} for endpoints not behind SearchAbuseGuard. */
export interface LangOpts {
  lang?: BackendLang;
}

function langInit(opts: LangOpts): { lang?: BackendLang } {
  return opts.lang === undefined ? {} : { lang: opts.lang };
}

export async function listProductHotSale(
  ctx: ApiFetchContext,
  input: ProductHotSaleInput,
  opts: LangOpts = {},
): Promise<PagedVo<SpuListVo>> {
  const raw = await apiFetch<PagedVo<SpuListVoRaw>>(
    ctx,
    buildProductHotSaleUrl(input),
    langInit(opts),
  );
  return normalizePaged(raw, normalizeSpu);
}

export async function listProductRecommend(
  ctx: ApiFetchContext,
  input: ProductRecommendInput,
  opts: LangOpts = {},
): Promise<PagedVo<ProductRecommendVO>> {
  const raw = await apiFetch<PagedVo<ProductRecommendVORaw>>(
    ctx,
    buildProductRecommendUrl(input),
    langInit(opts),
  );
  return normalizePaged(raw, normalizeRecommend);
}

/**
 * SearchAbuseGuard-protected endpoints accept an optional captchaToken that
 * the caller obtains by solving a Yandex SmartCaptcha after a prior request
 * returned ApiError("4601"). See `withCaptchaRetry` for the retry harness.
 *
 * `lang` controls the backend `lang` header — the backend localizes product
 * titles + spec attribute names per this header (issue #54). Omit it and
 * `buildHeaders` falls back to the brand DEFAULT_LOCALE (`ru_RU`). Callers
 * derive it from the UI locale via `toBackendLang(locale)`.
 */
export interface ProductReqOpts {
  captchaToken?: string;
  lang?: BackendLang;
}

/** @deprecated Prefer {@link ProductReqOpts}. Kept as an alias for callers that
 * only thread the captcha token. */
export type CaptchaOpts = ProductReqOpts;

// tsconfig has `exactOptionalPropertyTypes: true` (see packages/config/base.json),
// so `{ captchaToken: undefined }` is NOT assignable to `{ captchaToken?: string }`.
// Spread conditionally so each property only exists when actually set.
function reqInit(opts: ProductReqOpts): { captchaToken?: string; lang?: BackendLang } {
  return {
    ...(opts.captchaToken === undefined ? {} : { captchaToken: opts.captchaToken }),
    ...(opts.lang === undefined ? {} : { lang: opts.lang }),
  };
}

export async function searchProducts(
  ctx: ApiFetchContext,
  input: ProductSearchPagedInput,
  opts: ProductReqOpts = {},
): Promise<PagedVo<SpuListVo>> {
  const raw = await apiFetch<PagedVo<SpuListVoRaw>>(
    ctx,
    buildProductSearchPagedUrl(input),
    reqInit(opts),
  );
  return normalizePaged(raw, normalizeSpu);
}

export async function queryProductDetail(
  ctx: ApiFetchContext,
  input: ProductDetailInput,
  opts: ProductReqOpts = {},
): Promise<SpuDetail> {
  const raw = await apiFetch<SpuDetailVoRaw>(ctx, buildProductDetailUrl(input), reqInit(opts));
  return normalizeDetail(raw);
}

/**
 * POST /product/image/upload — returns an opaque `imageId` to seed the
 * subsequent /product/search/image query. Backend pins by channel; the
 * channel passed here MUST match the channel used in image-search.
 */
export async function uploadProductImage(
  ctx: ApiFetchContext,
  input: { imageBase64?: string; imageUrl?: string; channel: ChannelAlias },
  opts: ProductReqOpts = {},
): Promise<string> {
  return apiFetch<string>(ctx, "/product/image/upload", {
    method: "POST",
    body: {
      ...(input.imageBase64 ? { imageBase64: input.imageBase64 } : {}),
      ...(input.imageUrl ? { imageUrl: input.imageUrl } : {}),
      channel: toBackendChannel(input.channel),
    },
    ...reqInit(opts),
  });
}

/**
 * GET /product/image?id=X — echoes back the raw base64 the user uploaded
 * during image search (no `data:image/...;base64,` prefix). The backend
 * wraps it in `Result<String>`, so this returns the base64 payload string
 * (apiFetch unwraps `result`). Consumers must prepend a data-URL prefix
 * before feeding it to an `<img src>` — see ImageSearchBanner.
 */
export async function getProductImage(ctx: ApiFetchContext, imageId: string): Promise<string> {
  return apiFetch<string>(ctx, `/product/image?id=${encodeURIComponent(imageId)}`);
}

/** GET /product/search/image — paginated image-similarity search. No sort/filter. */
export async function searchProductsByImage(
  ctx: ApiFetchContext,
  input: ProductImageSearchInput,
  opts: ProductReqOpts = {},
): Promise<PagedVo<SpuListVo>> {
  const raw = await apiFetch<PagedVo<SpuListVoRaw>>(
    ctx,
    buildProductImageSearchUrl(input),
    reqInit(opts),
  );
  return normalizePaged(raw, normalizeSpu);
}

export async function queryProductPdpRecommend(
  ctx: ApiFetchContext,
  input: ProductPdpRecommendInput,
  opts: LangOpts = {},
): Promise<ProductPdpRecommendVO[]> {
  const raw = await apiFetch<ProductPdpRecommendVORaw[]>(
    ctx,
    buildProductPdpRecommendUrl(input),
    langInit(opts),
  );
  return (raw ?? []).map(normalizePdpRecommend);
}

export async function queryProductQcPhotos(
  ctx: ApiFetchContext,
  input: ProductQcPhotosInput,
): Promise<ProductQcPhotos> {
  const raw = await apiFetch<ProductQcPhotosRaw>(ctx, buildProductQcPhotosUrl(input));
  return normalizeQcPhotos(raw);
}
